import LiveLike from "@livelike/engagementsdk";
import { IUserProfile } from "@livelike/javascript";
import { useQuery } from "@tanstack/react-query";

import { appController } from "../stores/useAppStore";
import { getLocalStorage, getValueFromUrl } from "../utils";
import { AnalyticsService } from "../utils/analytics-service";
import { VisitType } from "../../../types";
import { useEffect } from "react";

interface InitResponse {
  readonly userProfile?: IUserProfile;
  readonly error: unknown;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

const LIVELIKE_CLIENT_ID = import.meta.env.VITE_LL_CLIENT_ID;
const LL_ENDPOINT = `${import.meta.env.VITE_LL_ENDPOINT}/`;
const KEY = `livelike-user-auth-${LIVELIKE_CLIENT_ID}`;

/**
 * Build a consistent storage strategy for LiveLike
 */
function createStorageStrategy(storedProfile?: IUserProfile) {
  return {
    get: () => localStorage.getItem(KEY),
    set: (profile: IUserProfile) => {
      const parseData = storedProfile
        ? storedProfile.custom_data
        : profile.custom_data;
      const existing = JSON.parse(parseData || "{}");
      const userVisitType = existing.userVisitType;

      localStorage.setItem(
        KEY,
        JSON.stringify({
          ...profile,
          custom_data: JSON.stringify({
            userVisitType: !userVisitType
              ? VisitType.FirstVisit
              : userVisitType === VisitType.FirstVisit
              ? VisitType.SecondVisit
              : VisitType.MultiVisit,
          }),
        })
      );
    },
  };
}

/**
 * Initialize LiveLike with an existing profile
 */
async function initWithProfile(profile: IUserProfile) {
  await LiveLike.init({
    clientId: LIVELIKE_CLIENT_ID,
    accessToken: profile.access_token,
    storageStrategy: createStorageStrategy(profile),
    endpoint: LL_ENDPOINT,
  });

  LiveLike.applyLocalization({
    en: { "widget.timeline.loadMore": "Load More" },
    fr: { "widget.timeline.loadMore": "Charger plus" },
  });

  return profile;
}

/**
 * Initialize LiveLike with a new access token
 */
async function initNewProfile() {
  const accessToken =
    getValueFromUrl(window.location.href, "accessToken") || "";

  const profile = (await LiveLike.init({
    clientId: LIVELIKE_CLIENT_ID,
    accessToken,
    storageStrategy: createStorageStrategy(),
    endpoint: LL_ENDPOINT,
  })) as IUserProfile;

  return profile;
}

export function useInit(initArg?: { skipInit?: boolean }): InitResponse {
  const {
    data: userProfile,
    error,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["init", LIVELIKE_CLIENT_ID],
    queryFn: async () => {
      const stored = getLocalStorage(KEY);
      if (stored) {
        return initWithProfile(JSON.parse(stored) as IUserProfile);
      }
      return initNewProfile();
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !initArg?.skipInit,
  });

  const updatedProfile = JSON.parse(getLocalStorage(KEY) || "{}");
  useEffect(() => {
    if (updatedProfile) appController.updateUserProfile(updatedProfile);
  }, [updatedProfile]);

  useEffect(() => {
    if (isLoading) return;

    if (userProfile) {
      const customData = JSON.parse(userProfile.custom_data || "{}");
      AnalyticsService.initialize({
        ll_profile_id: userProfile.id || "",
        custom_id: customData.userVisitType || "",
        isInitialized: true,
      });
    } else {
      AnalyticsService.initialize({
        isInitialized: false,
      });
    }
  }, [isLoading, userProfile]);

  return { userProfile: updatedProfile, error, isLoading, isError };
}
